import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Chip,
  Divider,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Area,
  AreaChart
} from 'recharts';
import EventIcon from '@mui/icons-material/Event';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import useSensorData from '../hooks/useSensorData';
import { format, addDays, isFuture } from 'date-fns';

const PredictionView = () => {
  const theme = useTheme();
  const { loading, data, error } = useSensorData();
  const [predictedData, setPredictedData] = useState([]);
  const [nextWashDate, setNextWashDate] = useState(null);
  const [confidenceRange, setConfidenceRange] = useState({ low: null, high: null });
  
  // Generate prediction data when actual data is loaded
  useEffect(() => {
    if (data?.sensorData && data.sensorData.length > 0) {
      // Get the last actual data point
      const lastActualDate = new Date(data.sensorData[data.sensorData.length - 1].timestamp);
      const lastValue = data.sensorData[data.sensorData.length - 1].m1;
      
      // For demo purposes, we're generating mock prediction data
      // In a real application, this would come from the backend prediction model
      const numPredictionDays = 60;
      const mockPredictions = [];
      
      // Generate prediction data with a declining trend and some randomness
      let currentValue = lastValue;
      const dailyDecline = 0.8; // Average daily decline
      
      for (let i = 1; i <= numPredictionDays; i++) {
        const date = addDays(lastActualDate, i);
        // Add some randomness to the decline
        const randomFactor = 0.5 + Math.random();
        currentValue = Math.max(0, currentValue - (dailyDecline * randomFactor));
        
        // Calculate confidence interval (wider as we go further in the future)
        const confidenceWidth = 5 + (i * 0.5);
        
        mockPredictions.push({
          timestamp: date,
          predicted: currentValue,
          upperBound: currentValue + confidenceWidth,
          lowerBound: Math.max(0, currentValue - confidenceWidth),
          formattedDate: format(date, 'MM/dd/yyyy')
        });
      }
      
      setPredictedData(mockPredictions);
      
      // Find when the predicted value crosses the threshold
      const threshold = data.threshold || 66;
      const crossingPoint = mockPredictions.find(point => point.predicted < threshold);
      
      if (crossingPoint) {
        setNextWashDate(new Date(crossingPoint.timestamp));
        
        // Set confidence range (±3 days in this example)
        setConfidenceRange({
          low: addDays(new Date(crossingPoint.timestamp), -3),
          high: addDays(new Date(crossingPoint.timestamp), 3)
        });
      }
    }
  }, [data]);
  
  // Combine actual and predicted data for visualization
  const combinedData = data?.sensorData 
    ? [
        ...data.sensorData.slice(-30).map(item => ({
          timestamp: new Date(item.timestamp),
          actual: item.m1,
          formattedDate: format(new Date(item.timestamp), 'MM/dd/yyyy')
        })),
        ...predictedData.map(item => ({
          timestamp: item.timestamp,
          predicted: item.predicted,
          upperBound: item.upperBound,
          lowerBound: item.lowerBound,
          formattedDate: item.formattedDate
        }))
      ]
    : [];
  
  const renderPredictionTooltip = (props) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }
    
    const data = props.payload[0].payload;
    
    return (
      <Box className="sensor-tooltip">
        <Typography variant="subtitle2">{data.formattedDate}</Typography>
        <Divider sx={{ my: 1 }} />
        {data.actual !== undefined && (
          <Typography variant="body2">
            <strong>Actual:</strong> {data.actual.toFixed(1)}
          </Typography>
        )}
        {data.predicted !== undefined && (
          <>
            <Typography variant="body2">
              <strong>Predicted:</strong> {data.predicted.toFixed(1)}
            </Typography>
            <Typography variant="body2">
              <strong>Range:</strong> {data.lowerBound.toFixed(1)} - {data.upperBound.toFixed(1)}
            </Typography>
          </>
        )}
      </Box>
    );
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">Error loading sensor data</Typography>
        <Typography>{error.message}</Typography>
      </Box>
    );
  }
  
  // Days until next wash
  const daysUntilWash = nextWashDate 
    ? Math.ceil((nextWashDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom>
        Cleaning Predictions
      </Typography>
      
      {/* Prediction Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Next Predicted Cleaning
                  </Typography>
                  <Typography variant="h4" component="div">
                    {nextWashDate ? format(nextWashDate, 'MMMM d, yyyy') : 'Calculating...'}
                  </Typography>
                  
                  {daysUntilWash !== null && (
                    <Box sx={{ mt: 1 }}>
                      <Chip 
                        label={`${daysUntilWash} days from now`} 
                        color={daysUntilWash < 14 ? "error" : daysUntilWash < 30 ? "warning" : "success"}
                        icon={<EventIcon />}
                      />
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ 
                  backgroundColor: theme.palette.primary.main + '20', 
                  p: 1, 
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <Typography variant="body2" color="textSecondary">
                    Confidence
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    85%
                  </Typography>
                </Box>
              </Box>
              
              {confidenceRange.low && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  <strong>Confidence Range:</strong> {format(confidenceRange.low, 'MMM d')} - {format(confidenceRange.high, 'MMM d, yyyy')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Prediction Insights
              </Typography>
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <TrendingDownIcon color="error" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Declining Trend Detected" 
                    secondary="Sensor values are declining at an average rate of 0.8 units per day"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CompareArrowsIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Comparison to Historical Pattern" 
                    secondary="Current decline is 15% faster than the previous period"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="success" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Post-Clean Performance" 
                    secondary="Last cleaning resulted in 78% light level restoration"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Prediction Chart */}
      <Paper sx={{ p: 2, height: 500, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Sensor Reading Forecast
        </Typography>
        <Box sx={{ height: '90%' }}>
          {combinedData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={combinedData}
                margin={{
                  top: 10,
                  right: 30,
                  left: 10,
                  bottom: 0,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(timestamp) => format(new Date(timestamp), 'MM/dd')}
                  type="number"
                  domain={['auto', 'auto']}
                  scale="time"
                />
                <YAxis />
                <Tooltip content={renderPredictionTooltip} />
                <Legend />
                
                {/* Reference line for threshold */}
                <ReferenceLine 
                  y={data?.threshold || 66} 
                  label={{ 
                    value: `Threshold: ${data?.threshold || 66}`, 
                    position: 'insideBottomRight',
                    fill: theme.palette.error.main
                  }} 
                  stroke={theme.palette.error.main} 
                  strokeDasharray="3 3" 
                />
                
                {/* Historical data line */}
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 6 }}
                  name="Actual Reading"
                />
                
                {/* Predicted data with confidence area */}
                <Area
                  type="monotone"
                  dataKey="upperBound"
                  stroke="transparent"
                  fill={theme.palette.secondary.main}
                  fillOpacity={0.1}
                  name="Confidence Range"
                />
                <Area
                  type="monotone"
                  dataKey="lowerBound"
                  stroke="transparent"
                  fill="transparent"
                  name=""
                />
                <Line
                  type="monotone"
                  dataKey="predicted"
                  stroke={theme.palette.secondary.main}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  name="Predicted Reading"
                />
                
                {/* Next wash date marker */}
                {nextWashDate && (
                  <ReferenceLine
                    x={nextWashDate.getTime()}
                    stroke={theme.palette.error.main}
                    strokeWidth={2}
                    label={{ 
                      value: 'Predicted Wash', 
                      position: 'insideTopRight',
                      fill: theme.palette.error.main
                    }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography>No data available for prediction</Typography>
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Additional Information */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Understanding the Prediction
        </Typography>
        
        <Typography paragraph>
          This prediction is based on historical sensor readings and cleaning patterns. The model analyzes the 
          rate of light level decrease over time and predicts when it will cross below the threshold value of 
          {data?.threshold || 66}, indicating that cleaning is needed.
        </Typography>
        
        <Typography paragraph>
          The confidence interval (shown as the shaded area) represents the range of possible values based on 
          the model's uncertainty. As we look further into the future, this range widens, indicating decreased 
          confidence in the exact prediction.
        </Typography>
        
        <Typography>
          The prediction is updated daily with new sensor readings, which allows the system to refine its 
          forecast as more data becomes available.
        </Typography>
      </Paper>
    </Box>
  );
};

export default PredictionView;